import { useQuery } from "@apollo/client/react";
import { Document } from "flexsearch";
import { useMemo } from "react";

import { getFragmentData } from "@/generated/graphql";
import { ActivityFragmentDoc, GetActivitiesDocument } from "@/generated/graphql/graphql";

export const useSearchActivities = (keyword: string, options?: { skip?: boolean }) => {
  const { data, loading } = useQuery(GetActivitiesDocument, {
    fetchPolicy: "cache-first",
    skip: options?.skip,
  });

  const activities = useMemo(() => getFragmentData(ActivityFragmentDoc, data?.activities), [data?.activities]);

  const index = useMemo(() => {
    const index = new Document({
      document: {
        id: "id",
        index: ["name", "hiragana", "katakana", "romaji", "tokenized"],
        store: ["id", "name", "hiragana", "katakana", "romaji", "tokenized"],
      },
      preset: "match",
      tokenize: "reverse",
    });

    if (!loading) {
      activities?.forEach((activity) => {
        index.add(activity);
      });
    }

    return index;
  }, [loading, activities]);

  return useMemo(() => {
    if (loading || !activities) return [];

    if (keyword.trim() !== "") {
      const results = index.search(keyword);

      // Preserve search ranking order and avoid type mismatch between numeric/string IDs
      const orderedIds: Array<string | number> = [];
      const seen = new Set<string>();
      for (const r of results as Array<{ result: Array<string | number> }>) {
        for (const id of r.result) {
          const key = String(id);
          if (!seen.has(key)) {
            seen.add(key);
            orderedIds.push(id);
          }
        }
      }

      const activityById = new Map(activities.map((activity) => [String(activity.id), activity] as const));
      return orderedIds
        .map((id) => activityById.get(String(id)))
        .filter((activity): activity is NonNullable<typeof activity> => Boolean(activity));
    }

    return activities;
  }, [loading, activities, keyword, index]);
};
